import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
  LinearProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Confetti from "react-confetti";

// Modern, subtle card component with softer transitions
const DraggableCard = styled(Card)(({ theme, status }) => ({
  height: "100%",
  cursor: status === "matched" ? "default" : "grab",
  backgroundColor:
    status === "matched"
      ? "rgba(237, 247, 237, 0.7)"
      : status === "incorrect"
        ? "rgba(253, 237, 237, 0.7)"
        : status === "selected"
          ? "rgba(232, 244, 253, 0.7)"
          : "#FFFFFF",
  borderLeft:
    status === "matched"
      ? "3px solid rgba(76, 175, 80, 0.7)"
      : status === "incorrect"
        ? "3px solid rgba(244, 67, 54, 0.7)"
        : status === "selected"
          ? "3px solid rgba(33, 150, 243, 0.7)"
          : "3px solid rgba(224, 224, 224, 0.5)",
  boxShadow:
    status === "matched"
      ? "0 2px 8px rgba(0,0,0,0.03)"
      : status === "incorrect"
        ? "0 2px 8px rgba(220,0,0,0.08)"
        : status === "selected"
          ? "0 4px 12px rgba(33,150,243,0.1)"
          : "0 2px 6px rgba(0,0,0,0.06)",
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  borderRadius: "8px",
  position: "relative",
  "&:hover": {
    boxShadow:
      status === "matched"
        ? "0 2px 8px rgba(0,0,0,0.03)"
        : "0 6px 14px rgba(0,0,0,0.1)",
  },
  "&:active": {
    cursor: status === "matched" ? "default" : "grabbing",
    transform: status === "matched" ? "none" : "translateY(-2px)",
  },
}));

// Enhanced drop zone with more subtle visual feedback
const DroppableZone = styled(Box)(({ theme, isOver, canDrop, isActive }) => ({
  position: "relative",
  padding: 1,
  borderRadius: 8,
  backgroundColor: isOver
    ? canDrop
      ? "rgba(76, 175, 80, 0.05)"
      : "rgba(244, 67, 54, 0.05)"
    : "transparent",
  border: isOver
    ? canDrop
      ? "1px dashed rgba(76, 175, 80, 0.4)"
      : "1px dashed rgba(244, 67, 54, 0.4)"
    : isActive
      ? "1px dashed rgba(0, 0, 0, 0.12)"
      : "1px dashed transparent",
  minHeight: "80px",
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  margin: "4px 0",
}));

// Main component
const Match = ({ flashcards, onComplete }) => {
  const [fronts, setFronts] = useState([]);
  const [backs, setBacks] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [incorrectPair, setIncorrectPair] = useState(null);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [moves, setMoves] = useState(0);
  const [incorrectMoves, setIncorrectMoves] = useState(0);
  const [gameStartTime, setGameStartTime] = useState(null);
  const [gameEndTime, setGameEndTime] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const [showFacit, setShowFacit] = useState(false);
  const [devMode, setDevMode] = useState(false);
  const [showError, setShowError] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [activeSide, setActiveSide] = useState(null); // Track which side is active for styling

  // Refs for tracking drag-and-drop
  const dragSourceRef = useRef(null);
  const dragTargetRef = useRef(null);

  useEffect(() => {
    initializeGame();
  }, [flashcards]);

  const initializeGame = () => {
    if (!flashcards || flashcards.length === 0) return;

    const shuffledFronts = [...flashcards].sort(() => Math.random() - 0.5);
    const shuffledBacks = [...flashcards].sort(() => Math.random() - 0.5);

    setFronts(shuffledFronts);
    setBacks(shuffledBacks);
    setSelectedCard(null);
    setMatchedPairs([]);
    setIncorrectPair(null);
    setMoves(0);
    setIncorrectMoves(0);
    setGameStartTime(Date.now());
    setGameEndTime(null);
    setShowError(false);
    setShowConfetti(false);
    setActiveSide(null);
  };

  // Replace your drag and drop handlers with these:
  const handleDragStart = (e, card, type) => {
    // Clear any incorrect pairs when starting to drag
    setIncorrectPair(null);
    setIsDragging(true);

    // Store the dragged item info
    setDraggedItem({ card, type });

    // Set some data for the drag operation
    e.dataTransfer.setData(
      "text/plain",
      JSON.stringify({
        id: type === "front" ? card.front : card.back,
        type,
      }),
    );

    // Set the drag image
    e.dataTransfer.effectAllowed = "move";

    // Keep track of the source
    dragSourceRef.current = { card, type };
  };

  const handleDragOver = (e, card, type) => {
    e.preventDefault();

    // Only allow dropping if the card is from the other side and not already matched
    const isFromOtherSide = draggedItem && draggedItem.type !== type;
    const isTargetMatched =
      type === "front"
        ? matchedPairs.includes(card.front)
        : matchedPairs.includes(
            flashcards.find((f) => f.back === card.back)?.front,
          );

    if (isFromOtherSide && !isTargetMatched) {
      e.dataTransfer.dropEffect = "move";
      setDragOverItem({ card, type });
    } else {
      e.dataTransfer.dropEffect = "none";
    }
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = (e, targetCard, targetType) => {
    e.preventDefault();
    setDragOverItem(null);
    setIsDragging(false);

    if (!draggedItem) return;

    // Only process drops between different sides (front to back or back to front)
    if (draggedItem.type === targetType) return;

    // Check if either card is already matched
    const isSourceMatched =
      draggedItem.type === "front"
        ? matchedPairs.includes(draggedItem.card.front)
        : matchedPairs.includes(
            flashcards.find((f) => f.back === draggedItem.card.back)?.front,
          );

    const isTargetMatched =
      targetType === "front"
        ? matchedPairs.includes(targetCard.front)
        : matchedPairs.includes(
            flashcards.find((f) => f.back === targetCard.back)?.front,
          );

    if (isSourceMatched || isTargetMatched) return;

    // Process the match attempt
    processMatchAttempt(
      draggedItem.card,
      targetCard,
      draggedItem.type,
      targetType,
    );

    // Reset drag state
    setDraggedItem(null);
    dragSourceRef.current = null;
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
    setIsDragging(false);
  };

  // Helper function to check if a card is matched
  const isCardMatched = (card, type) => {
    if (type === "front") {
      return matchedPairs.includes(card.front);
    } else {
      return matchedPairs.includes(
        flashcards.find((f) => f.back === card.back)?.front,
      );
    }
  };

  // Traditional click handling as fallback
  const handleCardClick = (card, type) => {
    // If the card is already matched, do nothing
    if (isCardMatched(card, type)) return;

    // If there's already a selected card
    if (selectedCard) {
      // If same type of card is clicked, deselect it
      if (selectedCard.type === type) {
        if (
          (type === "front" && selectedCard.card.front === card.front) ||
          (type === "back" && selectedCard.card.back === card.back)
        ) {
          setSelectedCard(null);
          return;
        }
      }

      // If different type, attempt to match
      if (selectedCard.type !== type) {
        processMatchAttempt(selectedCard.card, card, selectedCard.type, type);
        return;
      }
    }

    // No card was selected, or same type but different card
    setSelectedCard({ card, type });
  };

  const processMatchAttempt = (card1, card2, type1, type2) => {
    setMoves(moves + 1);

    // Determine which card is front and which is back
    const frontCard = type1 === "front" ? card1 : card2;
    const backCard = type1 === "back" ? card1 : card2;

    // Check if it's a match
    const isMatch =
      frontCard.front ===
      flashcards.find((f) => f.back === backCard.back)?.front;

    if (isMatch) {
      // Match found
      setMatchedPairs([...matchedPairs, frontCard.front]);
      setSelectedCard(null);

      // Trigger confetti for a successful match
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 1800);
    } else {
      // No match - show error feedback
      setIncorrectPair({
        front: frontCard.front,
        back: backCard.back,
      });
      setShowError(true);
      setIncorrectMoves(incorrectMoves + 1);

      // Reset incorrect pair after a delay
      setTimeout(() => {
        setIncorrectPair(null);
        setSelectedCard(null);
      }, 1200);
    }
  };

  useEffect(() => {
    if (matchedPairs.length === flashcards.length && flashcards.length > 0) {
      setGameEndTime(Date.now());
      setShowCompleteDialog(true);

      // Final celebration confetti
      setShowConfetti(true);

      if (onComplete) {
        onComplete({
          moves,
          incorrectMoves,
          time: (Date.now() - gameStartTime) / 1000,
        });
      }
    }
  }, [matchedPairs, flashcards]);

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // Then, modify your getCardStatus function to respect the isDragging state:
  const getCardStatus = (card, type) => {
    // When dragging is active, don't show incorrect states
    if (isDragging && incorrectPair) {
      // If card is part of incorrectPair but we're dragging, don't show it as incorrect
      if (
        (type === "front" && incorrectPair.front === card.front) ||
        (type === "back" && incorrectPair.back === card.back)
      ) {
        return "default";
      }
    }

    // For front cards
    if (type === "front") {
      if (matchedPairs.includes(card.front)) return "matched";
      if (!isDragging && incorrectPair && incorrectPair.front === card.front)
        return "incorrect";
      if (
        selectedCard &&
        selectedCard.type === "front" &&
        selectedCard.card.front === card.front
      )
        return "selected";
      return "default";
    }
    // For back cards
    else {
      const frontForThisBack = flashcards.find(
        (f) => f.back === card.back,
      )?.front;
      if (matchedPairs.includes(frontForThisBack)) return "matched";
      if (!isDragging && incorrectPair && incorrectPair.back === card.back)
        return "incorrect";
      if (
        selectedCard &&
        selectedCard.type === "back" &&
        selectedCard.card.back === card.back
      )
        return "selected";
      return "default";
    }
  };

  const findMatchForCard = (card, type) => {
    if (type === "front") {
      return flashcards.find((f) => f.front === card.front)?.back;
    } else {
      return flashcards.find((f) => f.back === card.back)?.front;
    }
  };

  // Check if a card is the current drag target
  const isDropTarget = (card, type) => {
    if (!dragOverItem) return false;
    return (
      (type === "front" && dragOverItem.card.front === card.front) ||
      (type === "back" && dragOverItem.card.back === card.back)
    );
  };

  // Check if drop is allowed
  const canDropOn = (card, type) => {
    if (!draggedItem) return false;

    // Must be different types (front/back)
    if (draggedItem.type === type) return false;

    // Target must not be already matched
    if (isCardMatched(card, type)) return false;

    return true;
  };

  // Calculate accuracy
  const accuracy =
    moves > 0 ? (((moves - incorrectMoves) / moves) * 100).toFixed(0) : 0;

  // Get the progress percentage
  const progressPercentage =
    flashcards.length > 0 ? (matchedPairs.length / flashcards.length) * 100 : 0;

  return (
    <Box sx={{ width: "100%", p: { xs: 1, sm: 2, md: 3 }, bgcolor: "#FAFAFA" }}>
      {/* Confetti effect for matches */}
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={400}
          gravity={0.5}
        />
      )}

      {/* Header with controls */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          p: { xs: 2, sm: 3 },
          bgcolor: "#FFFFFF",
          borderRadius: 2,
          boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 500,
            color: "#263238",
            fontSize: { xs: "1.25rem", md: "1.5rem" },
          }}
        >
          Matchningsspel
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={devMode}
                onChange={(e) => setDevMode(e.target.checked)}
                color="warning"
                size="small"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                Utvecklarläge
              </Typography>
            }
            sx={{ mr: { xs: 0, sm: 2 } }}
          />
          {devMode && (
            <Tooltip title={showFacit ? "Dölj facit" : "Visa facit"}>
              <IconButton
                onClick={() => setShowFacit(!showFacit)}
                color={showFacit ? "warning" : "default"}
                size="small"
                sx={{ mr: { xs: 0, sm: 1 } }}
              >
                {showFacit ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            startIcon={<ShuffleIcon />}
            onClick={initializeGame}
            size="small"
            sx={{
              mr: { xs: 0, sm: 1 },
              textTransform: "none",
              borderRadius: 1.5,
              borderColor: "rgba(0, 0, 0, 0.12)",
            }}
          >
            Blanda
          </Button>
          <Button
            variant="contained"
            startIcon={<RestartAltIcon />}
            onClick={initializeGame}
            color="primary"
            size="small"
            sx={{
              textTransform: "none",
              borderRadius: 1.5,
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.08)",
            }}
          >
            Återställ
          </Button>
        </Box>
      </Box>

      {/* Stats bar with progress */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 2, sm: 3 },
          bgcolor: "#FFFFFF",
          borderRadius: 2,
          boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            mb: 2,
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: "#546E7A" }}>
            <strong>Framsteg:</strong> {matchedPairs.length}/{flashcards.length}{" "}
            par
          </Typography>
          <Typography variant="body2" sx={{ color: "#546E7A" }}>
            <strong>Drag:</strong> {moves}{" "}
            {incorrectMoves > 0 && `(${incorrectMoves} fel)`}
          </Typography>
          <Typography variant="body2" sx={{ color: "#546E7A" }}>
            <strong>Tid:</strong>{" "}
            {formatTime(
              gameEndTime
                ? gameEndTime - gameStartTime
                : gameStartTime
                  ? Date.now() - gameStartTime
                  : 0,
            )}
          </Typography>
        </Box>

        {/* Progress bars */}
        <Box sx={{ mb: 1.5 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: "0.75rem", color: "#37474F" }}
            >
              Framsteg
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "0.75rem", color: "#37474F" }}
            >
              {Math.round(progressPercentage)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            color="success"
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: "rgba(0, 0, 0, 0.03)",
            }}
          />
        </Box>

        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: "0.75rem", color: "#37474F" }}
            >
              Korrekthet
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "0.75rem", color: "#37474F" }}
            >
              {accuracy}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={100 - (moves > 0 ? (incorrectMoves / moves) * 100 : 0)}
            color="primary"
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: "rgba(0, 0, 0, 0.03)",
            }}
          />
        </Box>
      </Box>

      {/* Brief instructions */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: "rgba(237, 247, 255, 0.7)",
          borderRadius: 2,
          border: "1px solid rgba(25, 118, 210, 0.1)",
          display: { xs: "block", md: "block" },
        }}
      >
        <Typography variant="body2" sx={{ color: "#1976D2" }}>
          Dra och släpp ett kort från varje kolumn för att matcha frågor med
          rätt svar, eller klicka på ett kort från varje kolumn.
        </Typography>
      </Box>

      {/* Matching area */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              bgcolor: "#FFFFFF",
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: "#1565C0",
                fontWeight: 500,
                fontSize: { xs: "1rem", md: "1.125rem" },
              }}
            >
              Frågor
            </Typography>
            <Box sx={{ flex: 1 }}>
              {fronts.map((card, index) => (
                <DroppableZone
                  key={`front-${index}`}
                  isOver={isDropTarget(card, "front")}
                  canDrop={canDropOn(card, "front")}
                  isActive={activeSide === "front"}
                >
                  <DraggableCard
                    status={getCardStatus(card, "front")}
                    onClick={() => handleCardClick(card, "front")}
                    draggable={!matchedPairs.includes(card.front)}
                    onDragStart={(e) => handleDragStart(e, card, "front")}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, card, "front")}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, card, "front")}
                  >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: "0.95rem",
                            lineHeight: 1.5,
                            flex: 1,
                            color: matchedPairs.includes(card.front)
                              ? "#4CAF50"
                              : "#37474F",
                          }}
                        >
                          {card.front}
                        </Typography>
                        {matchedPairs.includes(card.front) && (
                          <CheckCircleOutlineIcon
                            sx={{
                              color: "#4CAF50",
                              ml: 1,
                              fontSize: "1.25rem",
                            }}
                          />
                        )}
                        {incorrectPair &&
                          incorrectPair.front === card.front && (
                            <ErrorOutlineIcon
                              sx={{
                                color: "#F44336",
                                ml: 1,
                                fontSize: "1.25rem",
                              }}
                            />
                          )}
                      </Box>
                      {showFacit && !matchedPairs.includes(card.front) && (
                        <Box
                          sx={{
                            mt: 1.5,
                            pt: 1.5,
                            borderTop: "1px dashed rgba(0, 0, 0, 0.08)",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#FF9800",
                              fontStyle: "italic",
                              display: "flex",
                              alignItems: "center",
                              fontSize: "0.75rem",
                            }}
                          >
                            <VisibilityIcon
                              sx={{ mr: 0.5, fontSize: "0.75rem" }}
                            />
                            Facit: {findMatchForCard(card, "front")}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </DraggableCard>
                </DroppableZone>
              ))}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              bgcolor: "#FFFFFF",
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: "#FF9800",
                fontWeight: 500,
                fontSize: { xs: "1rem", md: "1.125rem" },
              }}
            >
              Svar
            </Typography>
            <Box sx={{ flex: 1 }}>
              {backs.map((card, index) => (
                <DroppableZone
                  key={`back-${index}`}
                  isOver={isDropTarget(card, "back")}
                  canDrop={canDropOn(card, "back")}
                  isActive={activeSide === "back"}
                >
                  <DraggableCard
                    status={getCardStatus(card, "back")}
                    onClick={() => handleCardClick(card, "back")}
                    draggable={
                      !matchedPairs.includes(
                        flashcards.find((f) => f.back === card.back)?.front,
                      )
                    }
                    onDragStart={(e) => handleDragStart(e, card, "back")}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, card, "back")}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, card, "back")}
                  >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: "0.95rem",
                            lineHeight: 1.5,
                            flex: 1,
                            color: matchedPairs.includes(
                              flashcards.find((f) => f.back === card.back)
                                ?.front,
                            )
                              ? "#4CAF50"
                              : "#37474F",
                          }}
                        >
                          {card.back}
                        </Typography>
                        {matchedPairs.includes(
                          flashcards.find((f) => f.back === card.back)?.front,
                        ) && (
                          <CheckCircleOutlineIcon
                            sx={{
                              color: "#4CAF50",
                              ml: 1,
                              fontSize: "1.25rem",
                            }}
                          />
                        )}
                        {incorrectPair && incorrectPair.back === card.back && (
                          <ErrorOutlineIcon
                            sx={{
                              color: "#F44336",
                              ml: 1,
                              fontSize: "1.25rem",
                            }}
                          />
                        )}
                      </Box>
                      {showFacit &&
                        !matchedPairs.includes(
                          flashcards.find((f) => f.back === card.back)?.front,
                        ) && (
                          <Box
                            sx={{
                              mt: 1.5,
                              pt: 1.5,
                              borderTop: "1px dashed rgba(0, 0, 0, 0.08)",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#FF9800",
                                fontStyle: "italic",
                                display: "flex",
                                alignItems: "center",
                                fontSize: "0.75rem",
                              }}
                            >
                              <VisibilityIcon
                                sx={{ mr: 0.5, fontSize: "0.75rem" }}
                              />
                              Facit: {findMatchForCard(card, "back")}
                            </Typography>
                          </Box>
                        )}
                    </CardContent>
                  </DraggableCard>
                </DroppableZone>
              ))}
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Error feedback */}
      <Snackbar
        open={showError}
        autoHideDuration={1200}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          sx={{
            borderRadius: 2,
            boxShadow: "0px 3px 8px rgba(0, 0, 0, 0.1)",
            "& .MuiAlert-icon": {
              fontSize: "1.25rem",
            },
          }}
        >
          Fel matchning! Försök igen.
        </Alert>
      </Snackbar>

      {/* Completion dialog */}
      <Dialog
        open={showCompleteDialog}
        onClose={() => setShowCompleteDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: "sm",
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            pb: 1,
            fontWeight: 600,
            color: "#2E7D32",
            fontSize: "1.5rem",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Grattis! 🎉
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 2 }}>
          <Typography variant="body1" sx={{ color: "#37474F", mb: 3 }}>
            Du slutförde matchningsspelet på:
          </Typography>
          <Box
            sx={{
              mb: 3,
              p: 3,
              bgcolor: "rgba(237, 247, 237, 0.7)",
              borderRadius: 2,
              border: "1px solid rgba(76, 175, 80, 0.2)",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                color: "#2E7D32",
                mb: 1,
              }}
            >
              {moves} drag och {formatTime(gameEndTime - gameStartTime)}
            </Typography>
            <Typography variant="body2" sx={{ color: "#37474F" }}>
              Korrekthet: {accuracy}% ({incorrectMoves} felaktiga försök)
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setShowCompleteDialog(false)}
            sx={{
              textTransform: "none",
              borderRadius: 1.5,
            }}
          >
            Stäng
          </Button>
          <Button
            onClick={initializeGame}
            variant="contained"
            color="primary"
            sx={{
              textTransform: "none",
              borderRadius: 1.5,
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.08)",
            }}
          >
            Spela igen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Match;
